import { logger } from '../../utils/logger.js'

const log = logger.create('geocode')

const PHOTON_REVERSE_URL = 'https://photon.komoot.io/reverse'

/** Abort the lookup if the network hasn't responded in this long. Keeps offline
 *  "stop recording" from hanging on the OS-level connection/DNS timeout. */
const LOOKUP_TIMEOUT_MS = 4000

/** Widen Photon's reverse search so empty backcountry points still resolve to a
 *  nearby feature carrying county/state (Photon's default tiny radius returns an
 *  empty list out there). Doesn't change ranking — the nearest feature still
 *  comes back first. */
const REVERSE_RADIUS_KM = 50

/** A returned feature within this distance is treated as "here" and named
 *  directly. Farther than this, we only trust its admin fields (county/state) —
 *  its name would be misleading as the location title. */
const NEAR_METERS = 300

function haversineMeters(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000
  const toRad = (d: number): number => (d * Math.PI) / 180
  const dLat = toRad(lat2 - lat1)
  const dLon = toRad(lon2 - lon1)
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)))
}

export interface ReverseGeocodeResult {
  name?: string | undefined
  street?: string | undefined
  housenumber?: string | undefined
  postcode?: string | undefined
  city?: string | undefined
  state?: string | undefined
  country?: string | undefined
  county?: string | undefined
  district?: string | undefined
  locality?: string | undefined
  countrycode?: string | undefined
  osmKey?: string | undefined
  osmValue?: string | undefined
  featureType?: string | undefined
  displayName: string
}

/**
 * A human label for "which administrative region is this", from whatever admin
 * fields are populated (coverage varies by country — `country`/`state` are
 * near-universal, `county`/`city` less so). Stays agnostic: only the US gets the
 * "X County" suffix; elsewhere the county field already holds the full local
 * name (e.g. "Haute-Savoie", "Landkreis Garmisch-Partenkirchen").
 */
function adminLabel(r: {
  county?: string | undefined
  city?: string | undefined
  state?: string | undefined
  country?: string | undefined
  countrycode?: string | undefined
}): string | undefined {
  let primary = r.county ?? r.city
  if (primary && r.county && r.countrycode === 'US') primary = `${r.county} County`
  const parts = [primary, r.state].filter((p): p is string => !!p)
  if (parts.length > 0) return parts.join(', ')
  return r.country ?? undefined
}

// Cache granularity. The old 0.01° (~1.1 km) bucketed clearly-different taps to
// one cached answer (so nearby points all returned the first one, with no new
// request). 0.0005° (~55 m) still dedupes re-taps on the same spot/feature but
// lets distinct points resolve independently.
function quantize(n: number): number {
  return Math.round(n * 2000) / 2000
}

const OSM_TYPE_LABELS: Partial<Record<string, Record<string, string>>> = {
  natural: {
    peak: 'Peak',
    saddle: 'Saddle',
    ridge: 'Ridge',
    cliff: 'Cliff',
    valley: 'Valley',
    water: 'Water',
    spring: 'Spring',
    glacier: 'Glacier',
    cave_entrance: 'Cave',
    hot_spring: 'Hot Spring',
    wetland: 'Wetland',
    wood: 'Forest',
    scrub: 'Scrub',
    grassland: 'Grassland',
    heath: 'Heath',
    fell: 'Fell',
    bare_rock: 'Bare Rock',
    scree: 'Scree',
  },
  highway: {
    path: 'Trail',
    track: 'Track',
    footway: 'Footway',
    bridleway: 'Bridleway',
    cycleway: 'Cycleway',
    service: 'Service Road',
    tertiary: 'Road',
    secondary: 'Road',
    primary: 'Road',
    trunk: 'Highway',
    motorway: 'Highway',
  },
  waterway: {
    river: 'River',
    stream: 'Stream',
    canal: 'Canal',
    ditch: 'Ditch',
    waterfall: 'Waterfall',
  },
  amenity: {
    shelter: 'Shelter',
    drinking_water: 'Water Source',
    parking: 'Parking',
    toilets: 'Restroom',
    campsite: 'Campsite',
  },
  tourism: {
    camp_site: 'Campsite',
    camp_pitch: 'Camp Pitch',
    viewpoint: 'Viewpoint',
    information: 'Information',
    alpine_hut: 'Alpine Hut',
    wilderness_hut: 'Wilderness Hut',
    picnic_site: 'Picnic Site',
  },
  leisure: {
    park: 'Park',
    nature_reserve: 'Nature Reserve',
  },
  place: {
    hamlet: 'Hamlet',
    village: 'Village',
    town: 'Town',
    city: 'City',
    suburb: 'Suburb',
    neighbourhood: 'Neighborhood',
    isolated_dwelling: 'Dwelling',
    locality: 'Locality',
  },
  boundary: {
    national_park: 'National Park',
    protected_area: 'Protected Area',
  },
}

function resolveFeatureType(osmKey?: string, osmValue?: string): string | undefined {
  if (!osmKey || !osmValue) return undefined
  return OSM_TYPE_LABELS[osmKey]?.[osmValue]
}

function buildDisplayName(r: Omit<ReverseGeocodeResult, 'displayName'>): string {
  const parts: string[] = []
  if (r.name) parts.push(r.name)
  else if (r.street) {
    const addr = r.housenumber ? `${r.housenumber} ${r.street}` : r.street
    parts.push(addr)
  }
  if (r.city && r.city !== r.name) parts.push(r.city)
  if (r.state) parts.push(r.state)
  return parts.length > 0 ? parts.join(', ') : (r.country ?? 'Unknown location')
}

class ReverseGeocodeService {
  private cache = new Map<string, ReverseGeocodeResult>()

  async lookup(lat: number, lon: number): Promise<ReverseGeocodeResult | null> {
    const qLat = quantize(lat)
    const qLon = quantize(lon)
    const key = `${qLat}:${qLon}`

    const cached = this.cache.get(key)
    if (cached) return cached

    // Don't even attempt a request when the platform reports no connectivity —
    // a bare fetch would otherwise block until the OS connection timeout.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return null

    try {
      // radius widens the search so empty backcountry points still resolve to a
      // nearby feature that carries county/state; lang=en normalises admin names
      // out of local script (e.g. Devanagari/Japanese) where OSM has them.
      const url = `${PHOTON_REVERSE_URL}?lat=${lat}&lon=${lon}&radius=${REVERSE_RADIUS_KM}&limit=1&lang=en`
      const response = await fetch(url, { signal: AbortSignal.timeout(LOOKUP_TIMEOUT_MS) })
      if (!response.ok) return null

      const data = await response.json()
      const features = data?.features
      if (!features || features.length === 0) return null

      const feature = features[0]
      const props = feature.properties ?? {}
      const county = props.county ?? undefined
      const city = props.city ?? props.town ?? props.village ?? undefined
      const state = props.state ?? undefined
      const country = props.country ?? undefined
      const countrycode = props.countrycode ?? undefined

      // How far is the returned feature from the tapped point? Photon widens to
      // the nearest feature within REVERSE_RADIUS_KM, which out in the
      // backcountry can be far — close enough to name directly, or only good for
      // its admin region.
      const coords = feature.geometry?.coordinates
      const distM =
        Array.isArray(coords) && coords.length >= 2
          ? haversineMeters(lat, lon, coords[1], coords[0])
          : 0

      let result: ReverseGeocodeResult
      if (distM <= NEAR_METERS) {
        const osmKey: string | undefined = props.osm_key ?? undefined
        const osmValue: string | undefined = props.osm_value ?? undefined
        result = {
          name: props.name ?? undefined,
          street: props.street ?? undefined,
          housenumber: props.housenumber ?? undefined,
          postcode: props.postcode ?? undefined,
          city,
          state,
          country,
          county,
          district: props.district ?? undefined,
          locality: props.locality ?? undefined,
          countrycode,
          osmKey,
          osmValue,
          featureType: resolveFeatureType(osmKey, osmValue),
          displayName: '',
        }
        result.displayName = buildDisplayName(result)
      } else {
        // Too far to name; trust only the admin region. Leave county/street unset
        // so the popup doesn't render a duplicate "County" line under the title.
        const label = adminLabel({ county, city, state, country, countrycode })
        if (!label) return null
        result = { state, country, countrycode, displayName: label }
      }

      this.cache.set(key, result)

      if (this.cache.size > 200) {
        const firstKey = this.cache.keys().next().value
        if (firstKey) this.cache.delete(firstKey)
      }

      return result
    } catch (err) {
      log.warn('Reverse geocode failed', err)
      return null
    }
  }
}

export const reverseGeocodeService = new ReverseGeocodeService()
